import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { api } from '../../api';

// Cache for countries data to prevent repeated API calls
let countriesCache = null;
let countriesPromise = null;

/**
 * Fetch all countries from the API with caching
 * @returns {Promise<Array>} Array of country objects
 */
const getCountries = async () => {
  if (countriesCache) {
    return countriesCache;
  }
  
  if (countriesPromise) {
    return countriesPromise;
  }
  
  countriesPromise = api.master.getActiveCountries()
    .then(response => {
      countriesCache = response.data?.countries || [];
      countriesPromise = null;
      return countriesCache;
    })
    .catch(error => {
      console.error('Error fetching countries:', error);
      countriesPromise = null;
      return [];
    });
  
  return countriesPromise;
};

/**
 * Find country by ID
 * @param {number|string} countryId - The country ID to search for
 * @returns {Promise<Object|null>} Country object or null if not found
 */
const findCountryById = async (countryId) => {
  if (!countryId) return null;
  
  try {
    const countries = await getCountries();
    return countries.find(country => country.id === parseInt(countryId)) || null;
  } catch (error) {
    console.error('Error finding country by ID:', error);
    return null;
  }
};

// Country flag mapping based on ISO country codes - More reliable than country names
const COUNTRY_FLAGS = {
  // Major countries
  'IN': '🇮🇳', // India
  'US': '🇺🇸', // United States
  'GB': '🇬🇧', // United Kingdom
  'CA': '🇨🇦', // Canada
  'AU': '🇦🇺', // Australia
  'DE': '🇩🇪', // Germany
  'FR': '🇫🇷', // France
  'JP': '🇯🇵', // Japan
  'CN': '🇨🇳', // China
  'KR': '🇰🇷', // South Korea
  'SG': '🇸🇬', // Singapore
  'AE': '🇦🇪', // UAE
  'SA': '🇸🇦', // Saudi Arabia
  'PK': '🇵🇰', // Pakistan
  'BD': '🇧🇩', // Bangladesh
  'LK': '🇱🇰', // Sri Lanka
  'NP': '🇳🇵', // Nepal
  'BT': '🇧🇹', // Bhutan
  'MM': '🇲🇲', // Myanmar
  'TH': '🇹🇭', // Thailand
  'MY': '🇲🇾', // Malaysia
  'ID': '🇮🇩', // Indonesia
  'PH': '🇵🇭', // Philippines
  'VN': '🇻🇳', // Vietnam
  'AF': '🇦🇫', // Afghanistan
  'IR': '🇮🇷', // Iran
  'IQ': '🇮🇶', // Iraq
  'TR': '🇹🇷', // Turkey
  'EG': '🇪🇬', // Egypt
  'ZA': '🇿🇦', // South Africa
  'NG': '🇳🇬', // Nigeria
  'KE': '🇰🇪', // Kenya
  'ET': '🇪🇹', // Ethiopia
  'BR': '🇧🇷', // Brazil
  'AR': '🇦🇷', // Argentina
  'MX': '🇲🇽', // Mexico
  'RU': '🇷🇺', // Russia
  'IT': '🇮🇹', // Italy
  'ES': '🇪🇸', // Spain
  'NL': '🇳🇱', // Netherlands
  'BE': '🇧🇪', // Belgium
  'CH': '🇨🇭', // Switzerland
  'AT': '🇦🇹', // Austria
  'SE': '🇸🇪', // Sweden
  'NO': '🇳🇴', // Norway
  'DK': '🇩🇰', // Denmark
  'FI': '🇫🇮', // Finland
  'PL': '🇵🇱', // Poland
  'CZ': '🇨🇿', // Czech Republic
  'HU': '🇭🇺', // Hungary
  'RO': '🇷🇴', // Romania
  'BG': '🇧🇬', // Bulgaria
  'GR': '🇬🇷', // Greece
  'CY': '🇨🇾', // Cyprus
  'IL': '🇮🇱', // Israel
  'JO': '🇯🇴', // Jordan
  'LB': '🇱🇧', // Lebanon
  'KW': '🇰🇼', // Kuwait
  'QA': '🇶🇦', // Qatar
  'BH': '🇧🇭', // Bahrain
  'OM': '🇴🇲', // Oman
  'YE': '🇾🇪', // Yemen
  'IE': '🇮🇪', // Ireland
  'PT': '🇵🇹', // Portugal
  'MA': '🇲🇦', // Morocco
  'DZ': '🇩🇿', // Algeria
  'TN': '🇹🇳', // Tunisia
  'LY': '🇱🇾', // Libya
  'SD': '🇸🇩', // Sudan
  'GH': '🇬🇭', // Ghana
  'UG': '🇺🇬', // Uganda
  'TZ': '🇹🇿', // Tanzania
  'RW': '🇷🇼', // Rwanda
  'ZM': '🇿🇲', // Zambia
  'ZW': '🇿🇼', // Zimbabwe
  'BW': '🇧🇼', // Botswana
  'NA': '🇳🇦', // Namibia
  'MU': '🇲🇺', // Mauritius
  'SC': '🇸🇨', // Seychelles
  'MG': '🇲🇬', // Madagascar
  'MV': '🇲🇻', // Maldives
  'FJ': '🇫🇯', // Fiji
  'NZ': '🇳🇿', // New Zealand
  'PG': '🇵🇬', // Papua New Guinea
  'SB': '🇸🇧', // Solomon Islands
  'VU': '🇻🇺', // Vanuatu
  'WS': '🇼🇸', // Samoa
  'TO': '🇹🇴', // Tonga
  'PW': '🇵🇼', // Palau
  'FM': '🇫🇲', // Micronesia
  'MH': '🇲🇭', // Marshall Islands
  'KI': '🇰🇮', // Kiribati
  'TV': '🇹🇻', // Tuvalu
  'NR': '🇳🇷', // Nauru
  // Additional countries
  'CL': '🇨🇱', // Chile
  'PE': '🇵🇪', // Peru
  'CO': '🇨🇴', // Colombia
  'VE': '🇻🇪', // Venezuela
  'EC': '🇪🇨', // Ecuador
  'BO': '🇧🇴', // Bolivia
  'PY': '🇵🇾', // Paraguay
  'UY': '🇺🇾', // Uruguay
  'GY': '🇬🇾', // Guyana
  'SR': '🇸🇷', // Suriname
  'GF': '🇬🇫', // French Guiana
  'HR': '🇭🇷', // Croatia
  'RS': '🇷🇸', // Serbia
  'BA': '🇧🇦', // Bosnia and Herzegovina
  'ME': '🇲🇪', // Montenegro
  'MK': '🇲🇰', // North Macedonia
  'AL': '🇦🇱', // Albania
  'XK': '🇽🇰', // Kosovo
  'SI': '🇸🇮', // Slovenia
  'SK': '🇸🇰', // Slovakia
  'EE': '🇪🇪', // Estonia
  'LV': '🇱🇻', // Latvia
  'LT': '🇱🇹', // Lithuania
  'BY': '🇧🇾', // Belarus
  'UA': '🇺🇦', // Ukraine
  'MD': '🇲🇩', // Moldova
  'AM': '🇦🇲', // Armenia
  'AZ': '🇦🇿', // Azerbaijan
  'GE': '🇬🇪', // Georgia
  'KZ': '🇰🇿', // Kazakhstan
  'UZ': '🇺🇿', // Uzbekistan
  'TM': '🇹🇲', // Turkmenistan
  'KG': '🇰🇬', // Kyrgyzstan
  'TJ': '🇹🇯', // Tajikistan
  'MN': '🇲🇳', // Mongolia
  'KP': '🇰🇵', // North Korea
  'KH': '🇰🇭', // Cambodia
  'LA': '🇱🇦', // Laos
  'BN': '🇧🇳', // Brunei
  'TW': '🇹🇼', // Taiwan
  'HK': '🇭🇰', // Hong Kong
  'MO': '🇲🇴', // Macau
};

/**
 * Helper function to get country flag image by country code
 * @param {string} countryCode - ISO country code (e.g., 'IN', 'US', 'GB')
 * @param {string} flagSize - Flag size (default: '48x36')
 * @returns {JSX.Element} Flag image element or default globe emoji
 */
export const getCountryFlagByCode = (countryCode, flagSize = '48x36') => {
  if (!countryCode) return <span>🌍</span>;
  return (
    <img 
      src={`https://flagsapi.com/${countryCode.toUpperCase()}/flat/${flagSize}.png`} 
      alt={`${countryCode} flag`}
      style={{ width: 'auto', height: '1.2em', verticalAlign: 'middle' }}
    />
  );
};

/**
 * Helper function to get country flag emoji by country name (fallback)
 * This is only used when country code is not available from API
 * @param {string} countryName - Name of the country
 * @returns {JSX.Element} Flag emoji or default globe emoji
 */
export const getCountryFlag = (countryName) => {
  if (!countryName) return <span>🌍</span>;
  
  // If no country code available, return default globe emoji
  // API should provide country_code, so this is just a fallback
  console.warn(`Country code not found for: ${countryName}. Please ensure API returns country_code field.`);
  return <span>🌍</span>;
};

/**
 * CountryFlag Component
 * 
 * A reusable component that displays country flag emojis with optional country names.
 * Supports 120+ countries with their respective flag emojis using ISO country codes.
 * Works with country IDs and fetches country data from API. Relies on API to provide country_code field.
 * 
 * @param {Object} props - Component props
 * @param {number|string} props.countryId - ID of the country to display flag for
 * @param {string} [props.countryName] - Optional country name (if provided, skips API call)
 * @param {string} [props.countryCode] - Optional ISO country code for direct flag lookup
 * @param {boolean} [props.showName=true] - Whether to show country name alongside flag
 * @param {string} [props.variant="body2"] - Typography variant for text display
 * @param {string} [props.color="textSecondary"] - Color for the text
 * @param {string} [props.component="span"] - Component type to render as
 * @returns {JSX.Element} Rendered country flag component
 */
const CountryFlag = React.memo(({ 
  countryId,
  countryName: providedCountryName,
  countryCode: providedCountryCode,
  showName = true, 
  variant = "body2", 
  color = "textSecondary",
  component = "span",
  ...props 
}) => {
  const [countryData, setCountryData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Effect to fetch country data based on countryId
  useEffect(() => {
    const fetchCountryData = async () => {
      if ((providedCountryName && providedCountryCode) || !countryId) {
        // If both name and code are provided or no ID, use provided data
        setCountryData(
          providedCountryName || providedCountryCode 
            ? { 
                country_name: providedCountryName, 
                country_code: providedCountryCode 
              } 
            : null
        );
        return;
      }

      setLoading(true);
      try {
        const country = await findCountryById(countryId);
        console.log('CountryFlag - Fetched country:', country);
        setCountryData(country);
      } catch (error) {
        console.error('Error fetching country data:', error);
        setCountryData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCountryData();
  }, [countryId, providedCountryName, providedCountryCode]);

  const renderFlag = useCallback(() => {
    if (loading) {
      return showName ? (
        <Typography variant={variant} color={color} component={component} {...props}>
          <span>🌍</span> Loading...
        </Typography>
      ) : (
        <span {...props}><span>🌍</span></span>
      );
    }

    if (!countryData) {
      return showName ? (
        <Typography variant={variant} color={color} component={component} {...props}>
          <span>🌍</span> Unknown
        </Typography>
      ) : (
        <span {...props}><span>🌍</span></span>
      );
    }

    // Use country code from API response (primary method)
    const countryCode = countryData.country_code || countryData.code;
    const countryNameForFlag = countryData.country_name;
    
    console.log('CountryFlag - Rendering with:', { countryCode, countryNameForFlag, countryData });
    
    let flag;
    if (countryCode) {
      // Primary: Use country code from API
      flag = getCountryFlagByCode(countryCode, '48x36');
      console.log('CountryFlag - Flag from code:', countryCode, '->', flag);
    } else {
      // Fallback: Log warning and use default
      console.warn('Country code not available from API for:', countryNameForFlag);
      flag = getCountryFlag(countryNameForFlag);
    }

    if (!showName) {
      return <span {...props}>{flag}</span>;
    }

    return (
      <Typography variant={variant} color={color} component={component} {...props}>
        {flag} {countryNameForFlag}
      </Typography>
    );
  }, [countryData, loading, showName, variant, color, component, props]);

  return renderFlag();
});

// PropTypes for better type checking and documentation
CountryFlag.propTypes = {
  countryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  countryName: PropTypes.string,
  countryCode: PropTypes.string,
  showName: PropTypes.bool,
  variant: PropTypes.string,
  color: PropTypes.string,
  component: PropTypes.string,
};

// Default props
CountryFlag.defaultProps = {
  countryId: null,
  countryName: null,
  countryCode: null,
  showName: true,
  variant: "body2",
  color: "textSecondary",
  component: "span",
};

export default CountryFlag;
